import type { PostMessageSubmission, PostMessageSubProvider } from './types';

/**
 * HubSpot iframe form detection.
 *
 * HubSpot embedded forms fire postMessage events with:
 *   { type: 'hsFormCallback', eventName: 'onFormSubmitted', id: '<formGuid>', data: { submissionValues, redirectUrl } }
 *
 * Trusted origins cover both the legacy `*.hubspot.com` iframe host and the
 * newer embed-components-app host `js-<hublet>.hsforms.net` (e.g. js-eu1, js-na1).
 *
 * Docs: https://legacydocs.hubspot.com/global-form-events
 */
export const HubSpotProvider: PostMessageSubProvider = {
    name: 'hubspot',
    trustedOrigins: /^https:\/\/[\w-]+\.(hubspot\.com|hsforms\.net)$/,
    trustSameOrigin: true,

    detect(data: Record<string, unknown>): boolean {
        return data?.type === 'hsFormCallback';
    },

    isSubmission(data: Record<string, unknown>): boolean {
        return data?.eventName === 'onFormSubmitted';
    },

    extractData(data: Record<string, unknown>, origin: string): PostMessageSubmission {
        const indicators = ['postmessage_hubspot_form_submitted'];
        const nested = data?.data as Record<string, unknown> | undefined;
        if (nested?.redirectUrl) {
            indicators.push('hubspot_has_redirect');
        }
        return {
            provider: 'hubspot',
            formId: (data?.id as string) || 'hubspot_iframe_form',
            applyFormIdWhitelist: typeof data?.id === 'string' && data.id !== '',
            indicators,
            origin,
        };
    },
};
